import React from "react";
import { useTheme } from "../context/ThemeContext";
import { Sun, Moon } from "lucide-react";

// Personal appearance control. Changing this only affects the current user —
// their choice is saved to their own profile and never forced on anyone else.
// Each card is a tiny LIVE preview: it carries data-brand-theme/data-theme for its
// brand and the mode you are in, so the real theme tokens (theme/animations.jsx)
// paint it — page, rail, panel, accent button and heading face. Nothing here
// duplicates palette values, so a theme change can't leave a stale swatch behind.
const INFO = {
  cyber:    { hint: "Default · neon glass" },
  clean:    { hint: "Light · professional" },
  bold:     { hint: "Editorial · high-contrast" },
  terminal: { hint: "Dark · developer" },
  glossy:   { hint: "Polished glass · aqua", isNew: true },
  neon:     { hint: "Electric · magenta + cyan", isNew: true },
};

function Preview({ brand, mode }) {
  return (
    <div data-brand-theme={brand} data-theme={mode} aria-hidden="true" style={{
      height: 78, borderRadius: 8, overflow: "hidden", display: "flex",
      background: "var(--t-page-glow), var(--t-page)", border: "1px solid var(--t-border-softer)",
      fontFamily: "var(--t-font)", marginBottom: 10,
    }}>
      <div style={{ width: 18, background: "var(--t-rail-bg)", borderRight: "1px solid var(--t-rail-border)", display: "flex", flexDirection: "column", alignItems: "center", gap: 5, paddingTop: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: 3, background: "var(--t-accent-grad)" }} />
        <span style={{ width: 8, height: 3, borderRadius: 2, background: "var(--t-rail-dim)" }} />
        <span style={{ width: 8, height: 3, borderRadius: 2, background: "var(--t-rail-dim)" }} />
      </div>
      <div style={{ flex: 1, padding: 7, display: "flex", flexDirection: "column", gap: 5, minWidth: 0 }}>
        <div style={{ fontFamily: "var(--t-font-head)", fontWeight: 700, fontSize: 10, color: "var(--t-text-primary)", textShadow: "var(--t-head-shadow)", whiteSpace: "nowrap", overflow: "hidden" }}>Ticket #26338</div>
        <div style={{ flex: 1, borderRadius: "var(--t-radius-sm)", border: "1px solid var(--t-border-soft)", background: "var(--t-gloss), var(--t-panel)", boxShadow: "var(--t-shadow-panel)", padding: "5px 6px", display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={{ height: 3, width: "80%", borderRadius: 2, background: "var(--t-text-muted)", opacity: 0.6 }} />
          <span style={{ height: 3, width: "55%", borderRadius: 2, background: "var(--t-text-muted)", opacity: 0.4 }} />
          <span style={{ alignSelf: "flex-end", marginTop: "auto", height: 10, width: 30, borderRadius: "var(--t-radius-sm)", background: "var(--t-accent-grad)", boxShadow: "var(--t-btn-shadow)" }} />
        </div>
      </div>
    </div>
  );
}

export default function ThemePicker() {
  const { brand, setBrand, mode, setMode, BRANDS, BRAND_LABELS } = useTheme();

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(170px,1fr))", gap: 12 }}>
        {BRANDS.map((key) => {
          const info = INFO[key] || { hint: "" };
          const on = brand === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setBrand(key)}
              aria-pressed={on}
              style={{
                textAlign: "left", cursor: "pointer", padding: 10, borderRadius: 12,
                background: "var(--t-panel-hi)",
                border: on ? "1px solid var(--t-accent)" : "1px solid var(--t-border-soft)",
                boxShadow: on ? "0 0 0 2px var(--t-accent)" : "none",
                transition: "border-color .15s, box-shadow .15s",
              }}
            >
              <Preview brand={key} mode={mode} />
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: "var(--t-text-primary)" }}>{(BRAND_LABELS && BRAND_LABELS[key]) || key}</span>
                {info.isNew && <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.06em", padding: "1px 6px", borderRadius: 99, background: "var(--t-accent-soft)", color: "var(--t-text-primary)" }}>NEW</span>}
              </div>
              <div style={{ fontSize: 11.5, color: "var(--t-text-label)", marginTop: 1 }}>{info.hint}</div>
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16 }}>
        <span style={{ fontSize: 13, color: "var(--t-text-label)", fontWeight: 600 }}>Mode</span>
        {["dark", "light"].map((m) => {
          const on = mode === m;
          return (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              style={{
                display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
                padding: "8px 14px", borderRadius: 999, fontSize: 13, fontWeight: 600,
                color: on ? "#fff" : "var(--t-text-body)",
                background: on ? "var(--t-accent-grad)" : "var(--t-panel-hi)",
                border: on ? "1px solid var(--t-accent)" : "1px solid var(--t-border-soft)",
                textTransform: "capitalize",
              }}
            >
              {m === "dark" ? <Moon size={14} /> : <Sun size={14} />} {m}
            </button>
          );
        })}
      </div>
    </div>
  );
}
